import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import eagleHeadLogo from '../assets/dummy_icon.png';
import StyledButton from '@/components/StyledHtmlElements/Button';
import { useEffect, useState } from 'react';
import { featureList } from '@/components/InfoSection/data';
import { LuGripHorizontal } from 'react-icons/lu';
import { useUser } from '@/hooks/user-user';
import allRoutes from '@/lib/routes';
import DEBLogo from '../assets/Deutscher_Eishockey_Bund_Logo.svg';

type StylesObject = {
  [key: string]: {
    desktop: string;
    mobile: string;
    base: string;
  };
};

const HomePage = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [toggleFeatureList, setToggleFeatureList] = useState(false);
  const { userIsSet } = useUser();

  const styles: StylesObject = {
    logo: {
      desktop: 'w-[10svh]',
      mobile: 'w-[15%] ',
      base: 'h-auto object-contain mt-2 rounded-[15%] shrink m-auto',
    },

    headerOne: {
      desktop: 'text-[3rem]',
      mobile: 'text-[2rem]',
      base: 'text-neutralLight uppercase bg-gradient-to-r from-neutralDark/80 to-transparent rounded-[2rem] px-6',
    },

    headerTwo: {
      desktop: 'ml-[16%] text-[3rem]',
      mobile: 'ml-[10%] text-[2rem]',
      base: 'bg-gradient-to-r from-primaryColor/50 to-transparent  px-6 rounded-full text-neutralLight uppercase',
    },

    headerThree: {
      desktop: 'px-40 text-2xl',
      mobile: 'px-10 text-xl',
      base: 'w-fit bg-gradient-to-r from-transparent via-neutralLight to-transparent  text-neutralDark mt-6 ml-auto mr-auto ',
    },

    loginButton: {
      desktop: 'text-[1.8rem]',
      mobile: 'text-[1.3rem]',
      base: ' ml-auto mb-6 mr-auto glassmorphism-Light !bg-neutralDark/40 px-[5%] glow-effect h-fit sticky top-0 text-neutralLight hover:text-neutralLight',
    },

    // TODO: remove if the feature buttons are not needed
    featureWrapper: {
      desktop: 'glassmorphism-Dark py-1  mt-10 ',
      mobile: 'py-1 relative h-[calc(0%-1rem)] mt-20 ' + (toggleFeatureList ? ' !h-[100%] mt-0' : ''),
      base: 'glassmorphism-Dark px-4 max-h-fit justify-between shadow-top-light group transition-all hidden',
    },
  };

  const applyStyle = ({ desktop, base, mobile }: StylesObject[keyof StylesObject]) => {
    return windowWidth < 768 ? mobile + ' ' + base : desktop + ' ' + base;
  };

  const scrollIntoView = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

    // update URL hash
    window.location.hash = id;
  };

  // Close Feature Buttons on click outside
  useEffect(() => {
    if (windowWidth < 768) {
      const closeFeatureButtons = () => {
        setTimeout(() => {
          toggleFeatureList && setToggleFeatureList(false);
        }, 25);
      };

      window.addEventListener('mousedown', closeFeatureButtons);

      return () => {
        window.removeEventListener('mousedown', closeFeatureButtons);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleFeatureList]);

  // Resize Listener
  useEffect(() => {
    // Resize Listener
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section id="hero-section" className="no-dark h-[100svh] flex flex-col overflow-hidden shadow-md mb-4">
        <article
          id="hero-article"
          className="flex flex-col basis-3/4 overflow-hidden grow bg-[url('/src/assets/Hero-banner.webp')] bg-cover bg-[center_top_100%]">
          {/* Logo */}
          <img id="icehawk-logo" src={eagleHeadLogo} alt="Ice Hockey Logo" className={applyStyle(styles.logo)} />

          <div className="group absolute bottom-4 right-4 z-50 bg-white/80 backdrop-blur-md rounded-lg pl-3 pr-1 hover:px-4 py-2 shadow-lg flex items-center gap-2 text-sm text-gray-800 overflow-hidden">
            <img src={DEBLogo} alt="DEB Logo" className="h-12 w-auto z-50" />
            <span className="whitespace-nowrap z-40 font-semibold max-w-0 opacity-0 group-hover:max-w-xs group-hover:opacity-100 transition-all duration-300 ease-in-out">
              In Kooperation mit dem Deutschen Eishockey-Bund
            </span>
          </div>

          <div className="flex flex-col md:mt-4 gap-2 pt-[3%] md:pt-[10%] p-2 grow">
            {/* Heading text */}
            <div className="flex flex-col gap-4 max-md:mt-auto mb-auto">
              <h1 className={applyStyle(styles.headerOne)}>Spieleranalyse</h1>
              <h2 className={applyStyle(styles.headerTwo)}>Leistungsbewertung</h2>

              <p className={applyStyle(styles.headerThree)}>KI-gestützte Plattform für den Erfolg im Eishockey.</p>
            </div>

            <StyledButton className={applyStyle(styles.loginButton)}>
              <Link to={userIsSet ? 'navigation' : allRoutes.login.route}>
                {userIsSet ? 'Weiter zur App' : 'Anmelden'}
              </Link>
            </StyledButton>
          </div>

          {/* Features Button links */}
          {/* TODO: remove <aside> tag and content if the feature buttons are not needed */}
          <aside id="ai-features" className={applyStyle(styles.featureWrapper)}>
            {/* Mobile slider button */}
            <h2
              id="feature-open-button"
              onClick={() => {
                setToggleFeatureList(!toggleFeatureList);
              }}
              className={
                'border-2 realtive bg-neutralDark text-neutralLight p-2 rounded-full m-auto w-fit transition-all mb-[1rem] -translate-y-[100%] md:hidden' +
                (toggleFeatureList ? ' hidden' : '')
              }>
              <LuGripHorizontal size="2rem" />
            </h2>

            {/* Feature Buttons */}
            <ul className="grid grid-flow-row auto-rows-auto gap-4 grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
              {featureList.map((feature) => (
                <li
                  key={feature.label}
                  id={feature.label}
                  onClick={() => scrollIntoView('feature-' + feature.label)}
                  className="flex flex-col items-center justify-center relative anim-scale ai-feature">
                  <p className={`round-button h-[4svh] round-button-animation ${feature.buttonStyleClass}`}>
                    {feature.icon}
                  </p>
                  <p>{feature.label}</p>
                </li>
              ))}
            </ul>
          </aside>
        </article>
      </section>

      {/* Features */}
      <section id="feature-section" className="p-4 flex flex-col gap-8 overflow-hidden">
        <h3 className="text-4xl font-bold text-center tracking-wider">Features</h3>
        <ul className="flex flex-wrap gap-16 justify-evenly">
          {featureList.map((feature) => (
            <li key={feature.label} className="md:w-[40%] w-full  dark:bg-muted/20 rounded">
              <div
                id={'feature-' + feature.label}
                className="h-full shadow-md flex flex-col gap-8 p-8 rounded-md feature-box">
                <div className="flex gap-4 justify-start items-center">
                  <h4 className={`h-[5svh] ${feature.buttonStyleClass} round-button `}>{feature.icon}</h4>
                  <p className="font-heading text-2xl">{feature.headline}</p>
                </div>

                <p>{feature.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;
